import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Plus, Rocket, ChevronRight, CheckCircle2, ScanText, FileSearch, Mail, Plug, Cpu, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { dashboard } from "../mock";

const iconMap = { ScanText, FileSearch, Mail, Plug };
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "GOOD MORNING" : h < 18 ? "GOOD AFTERNOON" : "GOOD EVENING"; };
const today = () => new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { onboarding, shortcuts } = dashboard;
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/dashboard/stats"), api.get("/agents/activity")]);
        setStats(s.data); setActivity(a.data);
      } catch {}
    })();
  }, []);

  const statCards = [
    { label: "Invoices generated", value: stats ? String(stats.invoices) : "0", sub: "All time" },
    { label: "Auto-processed", value: stats ? `${stats.auto_processed}%` : "0%", sub: "No human steps" },
    { label: "Net VAT", value: stats ? `AED ${stats.net_vat.toFixed(0)}` : "AED 0", sub: "Output tax" },
    { label: "Reported to FTA", value: stats ? String(stats.reported) : "0", sub: "MLS accepted" },
  ];

  return (
    <div className="space-y-5">
      <div className="gr-ring fade-up">
        <div className="gr-inner px-6 md:px-8 py-6 flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-1.5 text-xs font-semibold tracking-wide brand-gradient-text"><Sparkles size={14} className="text-violet-500" />{greeting()}</div>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">{user?.name} <span>👋</span></h1>
            <p className="text-sm text-gray-500 mt-1">{user?.company} · {today()}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/a/agents")} className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-lg transition"><Cpu size={16} /> View agents</button>
            <button onClick={() => navigate("/a/e-invoice")} className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:opacity-95 px-4 py-2 rounded-lg transition"><Zap size={16} /> New e-invoice</button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl card-soft border border-gray-100 p-6 fade-up">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white"><Rocket size={20} /></div>
            <div>
              <h3 className="font-semibold text-gray-900">Get your workspace ready</h3>
              <p className="text-sm text-gray-500">Complete these steps to unlock fully autonomous invoicing</p>
            </div>
          </div>
          <span className="text-sm text-gray-400">{onboarding.completed} of {onboarding.total} complete</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-5">
          {onboarding.steps.map((s) => (
            <button key={s.id} className={`flex items-center justify-between gap-2 px-4 py-3 rounded-xl border text-sm transition-all hover-lift ${s.active ? "border-indigo-300 bg-indigo-50/40 ring-1 ring-indigo-200" : "border-gray-200 hover:border-gray-300"}`}>
              <span className="flex items-center gap-2"><span className={`w-4 h-4 rounded-full border-2 ${s.active ? "border-indigo-500" : "border-gray-300"}`} /><span className={s.active ? "text-indigo-700 font-medium" : "text-gray-600"}>{s.label}</span></span>
              <ChevronRight size={15} className="text-gray-300" />
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 fade-up">
        {statCards.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl card-soft border border-gray-100 p-5 hover-lift">
            <div className="flex items-center justify-between"><span className="text-sm text-gray-500">{s.label}</span><span className="text-xs text-gray-300">{s.sub}</span></div>
            <div className="text-3xl font-bold text-gray-900 mt-3">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 fade-up">
        <div className="bg-white rounded-2xl card-soft border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div><h3 className="font-semibold text-gray-900">Agent activity</h3><p className="text-sm text-gray-500">Latest autonomous runs</p></div>
            <button onClick={() => navigate("/a/agents")} className="text-sm text-indigo-600 font-medium inline-flex items-center gap-1 hover:underline">View all <ChevronRight size={14} /></button>
          </div>
          {activity.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-14 h-14 rounded-full bg-teal-500 flex items-center justify-center text-white"><CheckCircle2 size={28} /></div>
              <p className="font-semibold text-gray-800 mt-4">All clear</p>
              <p className="text-sm text-gray-400 mt-1">Generate an e-invoice to see agents in action</p>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {activity.map((a, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white flex items-center justify-center shrink-0"><Zap size={14} /></div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-700"><b>{a.number}</b> · {a.scenario} · VAT {a.vat?.toFixed?.(2)} {a.currency}</p>
                    <p className="text-xs text-gray-400 truncate">{a.reasoning?.narrative || "Auto-generated & reported"}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl card-soft border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate("/a/agents")} className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-orange-400 flex items-center justify-center text-white hover-lift"><Sparkles size={18} /></button>
              <div><h3 className="font-semibold text-gray-900">Automation shortcuts</h3><p className="text-sm text-gray-500">Jump into your most-used workflows</p></div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {shortcuts.map((sc) => {
              const Icon = iconMap[sc.icon];
              const paths = { parse: "/a/parse", extract: "/a/extraction", inbox: "/a/inbox", integrations: "/a/settings" };
              return (
                <button key={sc.id} onClick={() => navigate(paths[sc.id] || "/a/agents")} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 hover:bg-gray-50 hover:border-gray-200 transition text-left">
                  <span className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500"><Icon size={17} /></span>
                  <span><span className="block text-sm font-medium text-gray-800">{sc.title}</span><span className="block text-xs text-gray-400">{sc.desc}</span></span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
